import React, { useState, useRef, useEffect } from 'react';
import { MediaFeatureModel, MediaType } from '../../../../fetch-data/graphql';
import {
  MediaCaption,
  MediaImage,
  MediaImageContainer,
  MediaImagePrint,
  MediaVideo,
  MediaWrapper,
  MediaYouTubeIframe,
} from './styles';

import { renderMarkdown } from '../ContentFeature/overrides/markdown';
import { isVideo, isYouTubeUrl, convertToYouTubeEmbedUrl } from './helpers';
import { useQueryParamValue } from '../../../../hooks/custom/use-query-param-value';
import isBrowser from 'is-browser';

interface MediaFeatureProps {
  feature: MediaFeatureModel;
  isTwoColumn?: boolean;
}

export const MediaFeature: React.FC<MediaFeatureProps> = ({
  feature,
  isTwoColumn = false,
}) => {
  const {
    mediaType,
    asset,
    altText,
    caption,
    posterImage,
    externalUrl,
    autoplay,
    loop,
    muted,
    showControls,
    forceMaxWidth,
  } = feature;

  const hasCaption = caption && caption.html;
  const isAVideo = isVideo(feature);
  const src = asset?._publishUrl || externalUrl;
  const isYouTube = isYouTubeUrl(src);
  const embedUrl = isYouTube && src ? convertToYouTubeEmbedUrl(src) : '';
  const printModeValue = useQueryParamValue('print-mode', '');
  const isPrintMode = Boolean(printModeValue);

  return (
    <>
      <MediaWrapper $isTwoColumn={isTwoColumn} $forceMaxWidth={forceMaxWidth}>
        {isYouTube && embedUrl ? (
          <MediaYouTubeIframe
            src={embedUrl}
            title={altText || 'YouTube video player'}
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
          />
        ) : (mediaType === MediaType.VIDEO && isAVideo) || isAVideo ? (
          <MediaVideo
            src={src}
            alt={altText || ''}
            autoPlay={autoplay}
            muted={muted}
            loop={loop}
            playsInline={true}
            controls={showControls}
            preload={autoplay ? 'auto' : 'metadata'}
            poster={posterImage?._publishUrl}
          />
        ) : (
          <MediaImageWithAspectRatio
            src={src || ''}
            alt={altText || ''}
            printMode={isPrintMode}
          />
        )}
        {hasCaption && (
          <MediaCaption>{renderMarkdown({ text: caption.html! })}</MediaCaption>
        )}
      </MediaWrapper>
    </>
  );
};

// Component to handle image loading and aspect ratio preservation
const MediaImageWithAspectRatio: React.FC<{
  src: string;
  alt: string;
  printMode?: boolean;
}> = ({ src, alt, printMode = false }) => {
  const isSSR = !isBrowser;
  const [isLoaded, setIsLoaded] = useState<boolean>(printMode || isSSR);
  const [aspectRatio, setAspectRatio] = useState<string>('16 / 9'); // Default placeholder ratio
  const imgRef = useRef<HTMLImageElement>(null);

  // Preload image to get dimensions before rendering
  useEffect(() => {
    if (printMode || isSSR) return undefined;
    
    const preloadImg = new Image();
    preloadImg.src = src;

    const handleLoad = () => {
      if (preloadImg.naturalWidth && preloadImg.naturalHeight) {
        // Set aspect ratio based on actual image dimensions
        setAspectRatio(
          `${preloadImg.naturalWidth} / ${preloadImg.naturalHeight}`
        );
        setIsLoaded(true);
      }
    };

    const handleError = () => {
      // On error, keep default aspect ratio
      setIsLoaded(true);
    };

    if (preloadImg.complete) {
      handleLoad();
      return undefined;
    } else {
      preloadImg.addEventListener('load', handleLoad);
      preloadImg.addEventListener('error', handleError);
      return () => {
        preloadImg.removeEventListener('load', handleLoad);
        preloadImg.removeEventListener('error', handleError);
      };
    }
  }, [src, printMode, isSSR]);

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  // In print/PDF mode, render immediately with natural layout (no aspect-ratio placeholder).
  if (printMode) {
    return (
      <MediaImagePrint src={src} alt={alt} loading='eager' decoding='sync' />
    );
  }

  return (
    <MediaImageContainer 
      style={{ aspectRatio } as React.CSSProperties}
      $isSSR={isSSR}
    >
      <MediaImage
        ref={imgRef}
        src={src}
        alt={alt}
        $isLoaded={isLoaded}
        onLoad={handleImageLoad}
        loading={isSSR ? 'eager' : 'lazy'}
        decoding={isSSR ? 'sync' : 'async'}
      />
    </MediaImageContainer>
  );
};
